import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Organization, OrganizationId, OrganizationMembership, OrganizationMemberWithDetails, OrganizationSubscriptionInfo } from '@sona/shared-model-common';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class OrganizationApiService {
    private readonly _http = inject(HttpClient);
    private readonly _baseUrl = '/api/organizations';

    /**
     * Get all organizations for the current user
     */
    getUserOrganizations(): Observable<Organization[]> {
        return this._http.get<Organization[]>(`${this._baseUrl}`);
    }

    /**
     * Get all organizations for the current user
     */
    getCurrentOrganization(): Observable<Organization> {
        return this._http.get<Organization>(`${this._baseUrl}/current`);
    }

    /**
     * Get organization by ID
     */
    getOrganization(id: OrganizationId): Observable<Organization> {
        return this._http.get<Organization>(`${this._baseUrl}/${id}`);
    }

    /**
     * Create a new organization
     */
    createOrganization(name: string): Observable<Organization> {
        return this._http.post<Organization>(this._baseUrl, { name });
    }

    /**
     * Update an organization
     */
    updateOrganization(id: OrganizationId, name: string): Observable<Organization> {
        return this._http.put<Organization>(`${this._baseUrl}/${id}`, { name });
    }

    /**
     * Delete an organization
     */
    deleteOrganization(id: OrganizationId): Observable<void> {
        return this._http.delete<void>(`${this._baseUrl}/${id}`);
    }

    /**
     * Switch current organization context
     */
    switchOrganization(organizationId: OrganizationId): Observable<void> {
        return this._http.post<void>(`/api/auth/switch-organization`, { organizationId });
    }

    /**
     * Get members of an organization
     */
    getOrganizationMembers(organizationId: OrganizationId): Observable<OrganizationMemberWithDetails[]> {
        return this._http.get<OrganizationMemberWithDetails[]>(`${this._baseUrl}/${organizationId}/members`);
    }

    /**
     * Invite users to an organization
     */
    inviteUsersToOrganization(organizationId: OrganizationId, emails: string[]): Observable<OrganizationMembership[]> {
        return this._http.post<OrganizationMembership[]>(`${this._baseUrl}/${organizationId}/invite`, { emails });
    }

    /**
     * Remove a user from an organization
     */
    removeUserFromOrganization(organizationId: OrganizationId, userId: string): Observable<void> {
        return this._http.delete<void>(`${this._baseUrl}/${organizationId}/members/${userId}`);
    }

    /**
     * Get subscription information for an organization
     */
    getOrganizationSubscription(organizationId: OrganizationId): Observable<OrganizationSubscriptionInfo> {
        return this._http.get<OrganizationSubscriptionInfo>(`${this._baseUrl}/${organizationId}/subscription`);
    }

    /**
     * Get Stripe customer portal URL for managing billing
     */
    getStripeCustomerPortalUrl(organizationId: OrganizationId): Observable<{ url: string }> {
        return this._http.post<{ url: string }>(`${this._baseUrl}/${organizationId}/billing/portal`, {});
    }

    /**
     * Upgrade organization subscription
     */
    upgradeSubscription(organizationId: OrganizationId, planName: string): Observable<{ url: string }> {
        return this._http.post<{ url: string }>(`${this._baseUrl}/${organizationId}/billing/upgrade`, { planName });
    }

    /**
     * Purchase add-ons for organization
     */
    purchaseAddOns(organizationId: OrganizationId, addOns: { additionalDomains?: number; additionalCredits?: number }): Observable<{ url: string }> {
        return this._http.post<{ url: string }>(`${this._baseUrl}/${organizationId}/billing/addons`, addOns);
    }
}
